import React, { useEffect, useRef } from 'react';
import { Box, Alert } from '@mui/material';

// Minimal shape of the Google Identity Services global this component needs -
// the full script isn't typed, and we only touch these two calls.
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
          }) => void;
          renderButton: (parent: HTMLElement, options: Record<string, unknown>) => void;
        };
      };
    };
  }
}

interface GoogleSignInButtonProps {
  onCredential: (idToken: string) => void;
  disabled?: boolean;
}

/**
 * Renders Google's own "Sign in with Google" button via the Google Identity
 * Services script (loaded in public/index.html). No domain/`hd` restriction -
 * any Google account can attempt sign-in (CLAUDE.md §2); access is decided
 * server-side by whether the email exists in the user table.
 */
export const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({ onCredential, disabled }) => {
  const buttonContainerRef = useRef<HTMLDivElement>(null);
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  // Callers (Login.tsx) pass an inline handler that's a new function identity
  // on every render. Route through a ref instead of putting onCredential in
  // the effect's deps below - otherwise every unrelated re-render (isLoading,
  // form state, ...) re-runs google.accounts.id.initialize()/renderButton(),
  // discarding whatever instance the user's in-flight click/popup was tied to
  // and silently losing the credential response (GSI_LOGGER's own "called
  // multiple times, only the last initialized instance will be used" warning).
  const onCredentialRef = useRef(onCredential);
  onCredentialRef.current = onCredential;

  useEffect(() => {
    if (!clientId || disabled) {
      return;
    }

    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 50; // ~5s at 100ms - covers a slow network without polling forever

    const tryRender = () => {
      if (cancelled) return;
      if (!window.google || !buttonContainerRef.current) {
        // The GSI script loads async/defer - retry until it's ready, but give up
        // eventually (blocked script, offline, ad-blocker) rather than polling forever.
        if (attempts++ < MAX_ATTEMPTS) {
          setTimeout(tryRender, 100);
        }
        return;
      }

      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: (response) => onCredentialRef.current(response.credential),
      });
      window.google.accounts.id.renderButton(buttonContainerRef.current, {
        theme: 'outline',
        size: 'large',
        width: 320,
        text: 'signin_with',
        locale: 'es',
      });
    };

    tryRender();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onCredential is read via onCredentialRef, deliberately excluded (see comment above)
  }, [clientId, disabled]);

  if (!clientId) {
    // Not configured yet (blocked on provisioning the OAuth client - CLAUDE.md §2)
    // - fail visibly in non-production so it isn't mistaken for a bug, but don't
    // show a scary alert to end users in production.
    if (process.env.NODE_ENV !== 'production') {
      return (
        <Alert severity="warning" sx={{ width: '100%', mt: 2 }}>
          Google Sign-In no está configurado (falta REACT_APP_GOOGLE_CLIENT_ID).
        </Alert>
      );
    }
    return null;
  }

  return <Box ref={buttonContainerRef} sx={{ display: 'flex', justifyContent: 'center', width: '100%', mt: 2 }} />;
};
